import { FormFieldComponent } from "../formFieldComponent";
import { fromEvent } from "rxjs";
import { distinctUntilChanged, pluck } from "rxjs/operators";
import { DataType } from "@se/platform/forms/enums/dataType";
import { Form } from "../form";
import { IMessagingService } from "../../services";

class TextboxComponent extends FormFieldComponent {

    private inputEl: HTMLInputElement;

    constructor(protected fieldEl: HTMLElement, messagingService: IMessagingService, protected form: Form) {
        super(fieldEl, messagingService, form);
        
        this.inputEl = fieldEl.querySelector<HTMLInputElement>(".js-textbox");

        // listen for the field changes from the user
        const onTextboxChanged$ = fromEvent(this.inputEl, "change").pipe(
            pluck("target", "value"),
            distinctUntilChanged()
        );
        
        onTextboxChanged$.subscribe(value => 
            this.setResponseValue(value)
        );

        // apply restriction on the input based on the data type
        this.addInputRestrictions();
    }

    // restrict input to numerical values based on the data type 
    private addInputRestrictions() {

        let filter = null;

        if (this.fieldDataType === DataType.Decimal) {
            filter = /^-?\d{0,12}(\.\d{0,2})?$/;
        }
        else if (this.fieldDataType === DataType.Integer) {
            filter = /^-?\d{0,9}$/
        }

        if (filter) {
            ["input", "keydown", "keyup", "mousedown", "mouseup", "select", "contextmenu", "drop"].forEach(event => {
                this.inputEl.addEventListener(event, function () {

                    // check if the input is valid, based on the filter for integers/decimals above
                    let inputEvent = this;
                    if (filter.test(inputEvent.value)) {
                        this.oldValue = inputEvent.value;
                    }
                    else if (this.hasOwnProperty("oldValue")) {
                        // if oldValue is set, it means the value didnt meet the filter, so reset it 
                        inputEvent.value = this.oldValue;
                    }
                    else {
                        // if the value doesnt meet the filter, and we didnt track its previous value then just reset the value
                        inputEvent.value = "";
                    }
                });
            });
        }
    }

    // update the value of this field in the DOM.
    protected setDisplayValue(value: any) {
        this.inputEl.value = value;
    }

    // update the underlying response of this field. Triggers a service notification.
    protected setResponseValue(value: any) {

        if (value != null && value != undefined) {
            // convert value to the correct data type before setting response value
            switch (this.fieldDataType) {
                case DataType.Integer:
                    super.setResponseValue(value ? parseInt(value) : null);
                    break;
                case DataType.Decimal:
                    super.setResponseValue(value ? parseFloat(value) : null);
                    break;
                default:
                    super.setResponseValue(value);
                    break;
            }
        }
    }
    
    public setFocus() {
        this.inputEl.focus();
    }

    public enable() {
        this.inputEl.disabled = false;
        this.inputEl.setAttribute("aria-disabled", "false");
        this.inputEl.classList.remove("form-field--disabled");
    }

    public disable() {
        this.inputEl.disabled = true;
        this.inputEl.setAttribute("aria-disabled", "true");
        this.inputEl.classList.add("form-field--disabled");
    }

};

export { TextboxComponent };